/**
 * SignLinkDialog — Physician / Nurse Sign Link compose + share dialog.
 *
 * Compose form on top: name + email + expires days + role (physician vs nurse).
 * Once created, shows the secure signing URL with a copy button.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Link as LinkIcon, PenTool, Stethoscope, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { useState } from "react";

function EmailStatusPanel({ meta, onResendEmail }) {
  const [email, setEmail] = useState(meta?.email || "");
  const [sending, setSending] = useState(false);
  const sent = meta?.email_status === "sent";
  const failed = meta?.email && meta?.email_status && meta.email_status !== "sent";

  const handleSend = async () => {
    setSending(true);
    try { await onResendEmail(email.trim()); } finally { setSending(false); }
  };

  return (
    <div className="space-y-2">
      {sent && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-sky-50 border border-sky-200 text-sky-900 text-xs" data-testid="sign-link-email-sent">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Emailed to <span className="font-medium">{meta.email}</span>. Ask them to check spam if it doesn't arrive within a few minutes.</span>
        </div>
      )}
      {failed && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="sign-link-email-failed">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Email to {meta.email} failed{meta.email_error ? `: ${meta.email_error}` : ""}. Copy the link above or retry below.</span>
        </div>
      )}
      <div className="flex gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Send link to email…"
          className="text-sm"
          data-testid="sign-link-resend-email-input"
        />
        <Button variant="outline" size="sm" onClick={handleSend} disabled={sending || !email.trim()} className="shrink-0" data-testid="sign-link-resend-email-btn">
          <Mail className="w-4 h-4 mr-1" />
          {sending ? "Sending…" : sent ? "Resend" : "Email link"}
        </Button>
      </div>
    </div>
  );
}

export function SignLinkDialog({
  open,
  onOpenChange,
  signLinkData,
  setSignLinkData,
  creatingSignLink,
  onCreateSignLink,
  createdSignLinkUrl,
  createdSignLinkMeta,
  onResendEmail,
  onCopySignLink,
}) {
  const isNurse = (createdSignLinkMeta?.role || signLinkData?.signer_role || "physician") === "nurse";
  const roleLabel = isNurse ? "Nurse" : "Physician";
  const nameLabel = `${roleLabel} Name`;
  const emailLabel = `${roleLabel} Email`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sign-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNurse ? <PenTool className="w-5 h-5 text-indigo-500" /> : <Stethoscope className="w-5 h-5 text-teal-500" />}
            {roleLabel} Signature Link
          </DialogTitle>
          <DialogDescription>
            Generate a secure link the {roleLabel.toLowerCase()} can open to sign this document — no login required on their end.
          </DialogDescription>
        </DialogHeader>

        {!createdSignLinkUrl ? (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-slate-100 text-xs font-medium">
              <button
                type="button"
                onClick={() => setSignLinkData((p) => ({ ...p, signer_role: "physician", physician_name: "" }))}
                className={`px-3 py-2 rounded-md transition-colors ${!isNurse ? "bg-white shadow-sm text-teal-700" : "text-slate-600"}`}
                data-testid="sign-link-role-physician"
              >
                <Stethoscope className="w-4 h-4 inline mr-1" />
                Physician
              </button>
              <button
                type="button"
                onClick={() => setSignLinkData((p) => ({ ...p, signer_role: "nurse", physician_name: "" }))}
                className={`px-3 py-2 rounded-md transition-colors ${isNurse ? "bg-white shadow-sm text-indigo-700" : "text-slate-600"}`}
                data-testid="sign-link-role-nurse"
              >
                <PenTool className="w-4 h-4 inline mr-1" />
                Agency Nurse
              </button>
            </div>

            <div className="space-y-2">
              <Label>{nameLabel} *</Label>
              <Input
                value={signLinkData?.physician_name || ""}
                onChange={(e) => setSignLinkData((p) => ({ ...p, physician_name: e.target.value }))}
                placeholder={isNurse ? "e.g., Jane Doe, RN" : "e.g., Dr. John Smith, MD"}
                data-testid="sign-link-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{emailLabel}</Label>
              <Input
                type="email"
                value={signLinkData?.physician_email || ""}
                onChange={(e) => setSignLinkData((p) => ({ ...p, physician_email: e.target.value }))}
                placeholder={isNurse ? "nurse@youragency.com" : "physician@example.com"}
                data-testid="sign-link-email-input"
              />
              <p className="text-xs text-slate-500">
                We'll email the secure signing link to this address. Leave blank to just copy the link and share it yourself.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Link Expires In</Label>
              <select
                value={signLinkData?.expires_days ?? 7}
                onChange={(e) => setSignLinkData((p) => ({ ...p, expires_days: Number(e.target.value) }))}
                className="h-9 w-full px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="sign-link-created-banner">
              Sign link created for <span className="font-medium">{createdSignLinkMeta?.name || signLinkData?.physician_name}</span>.
            </div>
            <div className="space-y-1">
              <Label>Secure Signing URL</Label>
              <div className="flex gap-2">
                <Input value={createdSignLinkUrl} readOnly className="text-xs bg-slate-50" data-testid="sign-link-url-input" />
                <Button variant="outline" size="sm" onClick={onCopySignLink} className="shrink-0" data-testid="sign-link-copy-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <EmailStatusPanel meta={createdSignLinkMeta} onResendEmail={onResendEmail} />
          </div>
        )}

        <DialogFooter>
          {!createdSignLinkUrl ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creatingSignLink}>
                Cancel
              </Button>
              <Button
                onClick={onCreateSignLink}
                disabled={creatingSignLink || !signLinkData?.physician_name}
                data-testid="sign-link-create-btn"
              >
                <LinkIcon className="w-4 h-4 mr-1" />
                {creatingSignLink ? (signLinkData?.physician_email ? "Sending…" : "Creating…") : (signLinkData?.physician_email ? "Create & Email Link" : "Create Link")}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
